import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Receipt,
  Person,
  Logout,
  Settings,
  Diamond,
  ReceiptLong,
  Folder,
  Add,
  AccountBalance,
  AttachMoney,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { transactionsAPI, categoriesAPI, banksAPI, Bank } from '../services/api';

const drawerWidth = 256;

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/' },
  { text: 'Transactions', icon: <Receipt />, path: '/transactions' },
  { text: 'Collections', icon: <Diamond />, path: '/collections' },
  { text: 'Bills', icon: <ReceiptLong />, path: '/bills' },
  { text: 'Folders', icon: <Folder />, path: '/folders' },
  { text: 'Settings', icon: <Settings />, path: '/settings' },
];

// ── Quick-create transaction widget ──────────────────────────────────────────
interface Category { id: number; name: string; color: string }

const QuickAddWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [form, setForm] = useState({
    amount: '',
    description: '',
    transaction_type: 'expense' as 'income' | 'expense',
    category_id: '',
    bank_id: '',
    date: new Date().toISOString().slice(0, 16),
  });

  const openDialog = async () => {
    setError('');
    setSuccess('');
    setOpen(true);
    try {
      const [catRes, bankRes] = await Promise.all([categoriesAPI.getAll(), banksAPI.getAll()]);
      setCategories(catRes.data.data || catRes.data || []);
      setBanks(bankRes.data.data || bankRes.data || []);
    } catch (_) {}
  };

  const handleSubmit = async () => {
    if (!form.amount || !form.category_id || !form.bank_id) {
      setError('Amount, category and bank are required');
      return;
    }
    try {
      setLoading(true);
      await transactionsAPI.create({
        amount: parseFloat(form.amount),
        description: form.description || (form.transaction_type === 'expense' ? 'Expense' : 'Income'),
        date: form.date,
        transaction_type: form.transaction_type,
        category_id: parseInt(form.category_id),
        bank_id: parseInt(form.bank_id),
      });
      setSuccess('Transaction added!');
      setForm({ amount: '', description: '', transaction_type: 'expense', category_id: '', bank_id: '', date: new Date().toISOString().slice(0, 16) });
      setTimeout(() => { setOpen(false); setSuccess(''); }, 1200);
    } catch (e: any) {
      setError(e.response?.data?.errors?.join(', ') || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Quick add transaction" placement="left">
        <Fab
          color="primary"
          aria-label="quick add transaction"
          onClick={openDialog}
          sx={{
            position: 'fixed',
            bottom: 28,
            right: 28,
            zIndex: 1300,
            background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
            boxShadow: '0 8px 24px rgba(99,102,241,0.4)',
            '&:hover': { boxShadow: '0 12px 32px rgba(99,102,241,0.5)', transform: 'scale(1.05)' },
            transition: 'all 0.2s ease',
          }}
        >
          <Add />
        </Fab>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AttachMoney sx={{ color: 'primary.main' }} />
            Quick Add Transaction
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Box display="flex" gap={1}>
                {(['expense', 'income'] as const).map(t => (
                  <Button
                    key={t}
                    variant={form.transaction_type === t ? 'contained' : 'outlined'}
                    color={t === 'income' ? 'success' : 'error'}
                    fullWidth
                    onClick={() => setForm(f => ({ ...f, transaction_type: t }))}
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    {t === 'income' ? '+ Income' : '− Expense'}
                  </Button>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Amount (₹)" type="number" size="small"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Description (optional)" size="small"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Bank</InputLabel>
                <Select value={form.bank_id} label="Bank"
                  onChange={e => setForm(f => ({ ...f, bank_id: e.target.value as string }))}>
                  {banks.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={form.category_id} label="Category"
                  onChange={e => setForm(f => ({ ...f, category_id: e.target.value as string }))}>
                  {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Date & Time" type="datetime-local" size="small"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Add />}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ── Main Layout ───────────────────────────────────────────────────────────────
const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); navigate('/login'); handleMenuClose(); };

  const activeLabel = menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard';

  const drawer = (
    <Box sx={{
      height: '100%',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: '#e2e8f0',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Brand */}
      <Box sx={{ px: 3, py: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 2,
          background: 'linear-gradient(135deg, #6366f1, #818cf8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
        }}>
          <AccountBalance sx={{ fontSize: 20, color: '#fff' }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ color: '#f1f5f9', fontWeight: 700, lineHeight: 1.1 }}>
            Expense
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>Tracker</Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2, mb: 1 }} />

      <List sx={{ px: 1.5, pt: 0.5, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={active}
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  borderRadius: 2,
                  py: 1.1,
                  color: active ? '#fff' : '#94a3b8',
                  background: active
                    ? 'linear-gradient(135deg, rgba(99,102,241,0.9) 0%, rgba(129,140,248,0.8) 100%)'
                    : 'transparent',
                  boxShadow: active ? '0 4px 12px rgba(99,102,241,0.35)' : 'none',
                  '&:hover': {
                    background: active
                      ? 'linear-gradient(135deg, rgba(99,102,241,0.9) 0%, rgba(129,140,248,0.8) 100%)'
                      : 'rgba(255,255,255,0.06)',
                    color: '#fff',
                  },
                  '&.Mui-selected': { backgroundColor: 'transparent' },
                  '&.Mui-selected:hover': { backgroundColor: 'transparent' },
                  transition: 'all 0.15s ease',
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* User section */}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2 }} />
      <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{
          width: 34, height: 34, fontSize: '0.85rem', fontWeight: 700,
          background: 'linear-gradient(135deg, #6366f1, #f43f5e)',
        }}>
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </Avatar>
        <Box flexGrow={1} overflow="hidden">
          <Typography variant="body2" sx={{ color: '#f1f5f9', fontWeight: 600, lineHeight: 1.2 }} noWrap>
            {user?.name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }} noWrap>
            {user?.email}
          </Typography>
        </Box>
        <Tooltip title="Logout">
          <IconButton size="small" onClick={handleLogout} sx={{ color: '#64748b', '&:hover': { color: '#f43f5e' } }}>
            <Logout fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* AppBar (mobile + desktop header) */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(148,163,184,0.15)',
          color: '#0f172a',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700, color: '#0f172a' }}>
            {activeLabel}
          </Typography>
          <IconButton onClick={handleMenuOpen} size="small">
            <Avatar sx={{
              width: 34, height: 34, fontSize: '0.85rem', fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1, #f43f5e)',
            }}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
          >
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{user?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* Floating quick-add widget */}
      <QuickAddWidget />
    </Box>
  );
};

export default Layout;
